import {
    AccordionMultipleProps,
    Content,
    Header,
    Item,
    Root,
    Trigger,
} from '@radix-ui/react-accordion';
import cx from 'classnames';
import React, { ReactNode, useCallback, useState } from 'react';

import { IconChevronDown } from '../icons/ChevronDown';

import style from './Accordion.module.css';

type BaseAccordionItemType = {
    content: ReactNode;
    icon?: ReactNode;
    subContent?: string;
    value: string;
    title: string;
};

type AccordionValue<T extends BaseAccordionItemType = BaseAccordionItemType> = Array<T['value']>;

export function useAccordion<ItemType extends BaseAccordionItemType = BaseAccordionItemType>(
    defaultValue: AccordionValue<ItemType> = [],
) {
    const [value, setValue] = useState(defaultValue);

    const onValueChange = useCallback((newValue: AccordionValue<ItemType>) => {
        setValue(newValue);
    }, []);

    return {
        value,
        setValue,
        onValueChange,
    };
}

type AccordionProps<ItemType extends BaseAccordionItemType = BaseAccordionItemType> =
    React.RefAttributes<HTMLDivElement> &
        Omit<AccordionMultipleProps, 'type'> & {
            HeaderElement?: 'div' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
            items: Array<ItemType>;
            onItemChange?: (change: { item: ItemType; index: number; isOpen: boolean }) => void;
            onValueChange: (value: AccordionValue<ItemType>) => void;
            size?: 'S' | 'M';
            value: AccordionValue<ItemType>;
            variant?: 'default' | 'card';
        };

export function Accordion<ItemType extends BaseAccordionItemType = BaseAccordionItemType>({
    HeaderElement = 'div',
    items,
    onItemChange,
    onValueChange,
    size = 'S',
    value,
    variant = 'default',
    ...props
}: AccordionProps<ItemType>) {
    const handleValueChange = (newValue: AccordionValue<ItemType>) => {
        if (onItemChange) {
            const opened = newValue.filter((val) => !value.includes(val));
            const closed = value.filter((val) => !newValue.includes(val));

            [...opened, ...closed].forEach((changedValue) => {
                const itemIndex = items.findIndex((item) => item.value === changedValue);
                if (itemIndex !== -1) {
                    onItemChange({
                        item: items[itemIndex],
                        index: itemIndex,
                        isOpen: opened.includes(changedValue),
                    });
                }
            });
        }

        onValueChange(newValue);
    };

    const isCard = variant === 'card';

    return (
        <Root
            className={cx(style.list, { [style.cardList]: isCard })}
            data-testid="Accordion"
            {...props}
            value={value}
            onValueChange={handleValueChange}
            type="multiple"
        >
            {items.map((item) => (
                <Item
                    key={item.value}
                    className={cx(style.item, { [style.cardItem]: isCard })}
                    value={item.value}
                >
                    <Header asChild>
                        <HeaderElement>
                            <Trigger
                                className={cx(style.title, {
                                    ['font-s-extrabold']: !isCard && size === 'S',
                                    ['font-m-extrabold']: !isCard && size === 'M',
                                    ['font-body-bold']: isCard,
                                    [style.cardTitle]: isCard,
                                })}
                            >
                                {isCard ? (
                                    <span className={style.titleContent}>
                                        {item.icon ? (
                                            <span className={style.icon}>{item.icon}</span>
                                        ) : null}
                                        {item.title}
                                    </span>
                                ) : (
                                    item.title
                                )}
                                <IconChevronDown className={style.chevron} aria-hidden />
                            </Trigger>
                        </HeaderElement>
                    </Header>
                    <Content
                        className={cx(style.content, {
                            [style.cardContent]: isCard,
                            ['font-xs-regular']: isCard,
                        })}
                        forceMount
                    >
                        {item.content}
                        {item.subContent ? (
                            <p className={cx('font-xs-regular', style.subContent)}>
                                {item.subContent}
                            </p>
                        ) : null}
                    </Content>
                </Item>
            ))}
        </Root>
    );
}
